import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import { AnswerFeedbackReason } from "ai-assistant/feedback/answer-feedback-reason";
import { z } from "zod";

export type AnswerFeedbackJSON = z.infer<typeof AnswerFeedbackSchema.AnswerFeedbackJSONZ>;
export type AnswerFeedbackParams = z.infer<typeof AnswerFeedbackSchema.AnswerFeedbackZ>;

/** A user's own report on one answer, as the server echoes it and replays it with the conversation. */
export default class AnswerFeedbackSchema {
  public static readonly AnswerFeedbackJSONZ = z.object({
    reason: z.enum(AnswerFeedbackReason),
    comment: z.string().nullish(),
    reportedAt: z.iso.datetime({ offset: true }),
  });

  public static readonly AnswerFeedbackZ = z.object({
    reason: z.enum(AnswerFeedbackReason),
    comment: z.string().optional(),
    reportedAt: z.custom<AHDate>(),
  });

  public static validate(values: unknown): AnswerFeedbackParams {
    return parseOrThrow(AnswerFeedbackSchema.AnswerFeedbackZ, values, {
      logError: true,
      schemaName: "AnswerFeedbackSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  /** Decodes JSON that has already been validated as part of a larger parse. */
  public static decodeParsed(json: AnswerFeedbackJSON): AnswerFeedbackParams {
    return {
      reason: json.reason,
      comment: json.comment ?? undefined,
      reportedAt: new AHDate(json.reportedAt),
    };
  }

  public static deserialize(json: AnswerFeedbackJSON): AnswerFeedbackParams {
    if (isNullOrUndefined(json)) return json;
    return AnswerFeedbackSchema.decodeParsed(parseOrThrow(AnswerFeedbackSchema.AnswerFeedbackJSONZ, json, {
      logError: true,
      schemaName: "AnswerFeedbackSchema",
      operation: "deserialize",
      payloadSample: json,
    }));
  }
}
